import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SurveyPreviewTable } from '../controls/SurveyPreviewTable';
import { SurveyApprovalForm } from '../controls/SurveyApprovalForm';
import { checkLoggedIn } from '../../functions/CookieHelper';
import { batterySummary, isGammaSurvey } from '../../surveyCompleteness';
import { surveyQaBands, WTOTAL_TOLERANCE_FRACTION } from '../../surveyQa';
import { formatDate, formatTime } from '../../i18n/format';

export const SurveyApproval = () => {
    const { t } = useTranslation();
    const [searchParams, setSearchParams] = useSearchParams();
    const [loggedIn] = React.useState(checkLoggedIn());
    const [customerMap, setCustomerMap] = useState({});
    const [selectedSurveyReference, setSelectedSurveyReference] = useState({});
    const [currentSurvey, setCurrentSurvey] = useState({});
    const [wTotal, setWTotal] = useState(15.0);
    const [wTolerance, setWTolerance] = useState(15.0 * WTOTAL_TOLERANCE_FRACTION);
    const [approvedLines, setApprovedLines] = useState([]);
    const [gridCorrections, setGridCorrections] = useState([]);
    const [angleDisplay, setAngleDisplay] = useState(null);
    const [gridCorrection, setGridCorrection] = useState(null);
    const [surveyNotFound, setSurveyNotFound] = useState(false);

    const handleSurveySelect = (surveyReference) => {
        setSelectedSurveyReference(surveyReference);
    }

    const handleApproveSubmit = (selectedProject, selectedDrillhole) => {
        if (!selectedSurveyReference.key || !selectedProject || !selectedDrillhole) {
            return;
        }

        fetch('api/projects/approvesurvey', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                key: selectedSurveyReference.key,
                project: selectedProject,
                drillhole: selectedDrillhole,
                approvedLines: approvedLines
            })
        })
            .then(response => response.json())
            .then(data => {

                setCustomerMap(data);
                setSelectedSurveyReference({});
                setCurrentSurvey({});
            });
    }

    const handleSurveyDelete = () => {
        fetch('api/projects/deletesurvey', {
            method: 'DELETE',
            headers: {
                'Content-Type': 'application/json',
                'survey': selectedSurveyReference.key
            }
        })
            .then(response => response.json())
            .then(data => {
                setCustomerMap(data);
                setSelectedSurveyReference({});
                setCurrentSurvey({});
            }
            );
    }

    const handleApproveLines = (lineIndexes) => {
        setApprovedLines(lineIndexes);
    }

    useEffect(() => {
        console.log('SA_effect')
        if (!loggedIn) {
            window.location.href = '/login';
        }
        else {
            const fetchData = async () => {
                const res = await fetch('api/projects', {
                    method: 'GET'
                });
                const data = await res.json();
                setCustomerMap(data);
            }
            fetchData();
        }
    }, [loggedIn]);

    useEffect(() => {
        const fetchSettings = async () => {
            try {
                const response = await fetch('api/identity/customersettings');
                if (response.ok) {
                    const settings = await response.json();
                    setGridCorrections(settings.gridCorrections || []);
                    setAngleDisplay(settings.angleDisplay || null);
                }
            } catch (error) {
                console.error('Error fetching customer settings:', error);
            }
        };
        fetchSettings();
    }, []);

    useEffect(() => {
        const surveyParam = searchParams.get('survey');
        if (surveyParam && customerMap.unallocatedSurveys) {
            // The notification email links by file name; the drillhole view's "Return to
            // approval" links by fileId, because file names are not unique and a returned
            // survey can share one with a survey already in the queue.
            const match = customerMap.unallocatedSurveys.find(s => s.fileId === surveyParam)
                || customerMap.unallocatedSurveys.find(s => s.fileName === surveyParam);
            if (match) {
                setSelectedSurveyReference(match);
                setSearchParams({});
                setSurveyNotFound(false);
            } else if (customerMap.unallocatedSurveys.length >= 0) {
                setSurveyNotFound(true);
            }
        }
    }, [customerMap.unallocatedSurveys, searchParams, setSearchParams]);

    useEffect(() => {
        if (currentSurvey && currentSurvey.Created && gridCorrections.length > 0) {
            const surveyDate = new Date(currentSurvey.Created);
            const sortedCorrections = [...gridCorrections].sort((a, b) =>
                new Date(b.appliesFrom) - new Date(a.appliesFrom)
            );
            const applicable = sortedCorrections.find(c => new Date(c.appliesFrom) <= surveyDate);
            setGridCorrection(applicable || null);
        } else {
            setGridCorrection(null);
        }
    }, [currentSurvey, gridCorrections]);

    useEffect(() => {
        const loadSurvey = async () => {
            const response = await fetch('api/surveys/' + selectedSurveyReference.fileId, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json'
                }
            });
            const data = await response.json();
            // The bands depend on the tool class, and MiNS Memory's are neither
            // the same Earth rate nor the same shape as everyone else's.
            const bands = surveyQaBands(data);
            setWTotal(bands.expectedWt);
            setWTolerance(bands.wTolerance);
            setCurrentSurvey(data);
        }
        if (selectedSurveyReference.key) {
            loadSurvey();

        }
    }, [selectedSurveyReference]);


    return (
        <div>
            <h1>{t('approval.title')}</h1>
            {surveyNotFound && (
                <div className="alert alert-info alert-dismissible" role="alert">
                    {t('approval.surveyNotFound')}
                    <button type="button" className="btn-close" onClick={() => setSurveyNotFound(false)}></button>
                </div>
            )}
            <hr />
            <div className="row">
                <div className="col-lg-3">
                    <h3>{t('approval.unallocatedSurveys')}</h3>
                    <div className="list-group">
                        {customerMap.unallocatedSurveys && customerMap.unallocatedSurveys.map((survey) => {
                            // Survey filenames are long and share a common prefix,
                            // so the selected one needs a solid highlight to be
                            // picked out at a glance rather than a subtle one.
                            const isSelected = survey.key === selectedSurveyReference.key;
                            return (
                                <button key={survey.key}
                                    onClick={() => handleSurveySelect(survey)}
                                    aria-current={isSelected ? 'true' : undefined}
                                    className={`list-group-item list-group-item-action d-flex justify-content-between align-items-start gap-2 text-start${isSelected ? ' active' : ''}`}>
                                    <h5 className="mb-0 text-break">{survey.fileName}</h5>
                                    <h6 className="mb-0 flex-shrink-0 text-nowrap">{formatDate(survey.uploaded)}</h6>
                                </button>
                            );
                        })}
                    </div>
                </div>
                <div className="col-lg-9">
                    {/* container-fluid, not container: a fixed-width .container
                        nested inside a column re-applies its own max-width and
                        pinches the panel in further than the column already does. */}
                    <div className="container-fluid">
                        <div className="row">
                            <h2>{currentSurvey?.Name}</h2>
                        </div>
                        <div className="row">
                            <div className="col">
                                {currentSurvey.SavedShots && (
                                    <div>
                                        {/* A gamma log has no shots by design, so a "0" here would
                                            read as something having gone wrong. */}
                                        {currentSurvey.SavedShots && !isGammaSurvey(currentSurvey) &&
                                            <h5>{t('approval.savedShotCount')} {currentSurvey.SavedShots.length}</h5>}
                                        {currentSurvey.Created && <h5>{t('approval.surveyStarted')} {formatDate(currentSurvey.Created)} {formatTime(currentSurvey.Created)}</h5>}
                                        {currentSurvey.LastModified && <h5>{t('approval.surveyFinished')} {formatDate(currentSurvey.LastModified)} {formatTime(currentSurvey.LastModified)}</h5>}
                                        <h5>{t('drillholes.latitudeLabel')} {currentSurvey.Latitude ? currentSurvey.Latitude.toFixed(6) : '0.000000'}</h5>
                                        <h5>{t('drillholes.longitudeLabel')} {currentSurvey.Longitude ? currentSurvey.Longitude.toFixed(6) : '0.000000'}</h5>
                                        {batterySummary(currentSurvey) && <h5>{t('approval.battery')} {batterySummary(currentSurvey)}</h5>}
                                        {currentSurvey.DeviceName && <h5>{t('tableTab.device')} {currentSurvey.DeviceName}</h5>}
                                        {currentSurvey.DeviceId !== undefined && currentSurvey.DeviceId !== null && <h5>{t('tableTab.deviceId')} {currentSurvey.DeviceId}</h5>}
                                        {currentSurvey.AppVersion && <h5>{t('tableTab.appVersion')} {currentSurvey.AppVersion}</h5>}
                                        {/* Continuous tools only. Read-only here; the control that
                                            changes it sits with the shot table, which is what it
                                            governs. Absent AzimuthMode means Northseek. */}
                                        {(currentSurvey.DeviceName || '').startsWith('FastTrak') &&
                                            <h5>{t('azimuthDatum.label')} {(currentSurvey.AzimuthMode || '').toUpperCase() === 'REFERENCE'
                                                ? t('azimuthDatum.reference', { azimuth: Number(currentSurvey.StartAzimuth ?? 0).toFixed(2) })
                                                : t('azimuthDatum.northseek')}</h5>}
                                    </div>
                                )}
                            </div>
                            <div className="col">
                                {selectedSurveyReference.key && < SurveyApprovalForm
                                    customerMap={customerMap}
                                    selectedSurvey={selectedSurveyReference}
                                    handleApproveSubmit={handleApproveSubmit}
                                    handleSurveyDelete={handleSurveyDelete}
                                />}
                            </div>
                        </div>
                        <div className="row">
                            <div className="col">
                                {gridCorrection && (
                                    <div className="alert alert-info" role="alert">
                                        <strong>{t('tableTab.gridCorrection')}</strong> {t('tableTab.gridCorrectionValue', { correction: gridCorrection.correction, date: formatDate(gridCorrection.appliesFrom) })}
                                    </div>
                                )}
                                {currentSurvey && < SurveyPreviewTable selectedSurvey={currentSurvey} surveyKey={selectedSurveyReference.fileId} industry={customerMap.industry} angleDisplay={angleDisplay} WTotal={wTotal} WTolerance={wTolerance} GTotal={1.0} approvedLinesChanged={handleApproveLines} gridCorrection={gridCorrection} onSurveyUpdated={setCurrentSurvey} />}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

}
